import { DOCUMENT, inject, Injectable, signal } from '@angular/core';

export type Theme = 'light' | 'dark';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly storageKey = 'cysm.theme';

  readonly theme = signal<Theme>('light');

  initialize(): Theme {
    const next = this.getStoredTheme() ?? this.getSystemTheme();
    this.applyTheme(next);
    return next;
  }

  setTheme(theme: Theme): void {
    this.applyTheme(theme);
    this.storeTheme(theme);
  }

  toggle(): void {
    this.setTheme(this.theme() === 'dark' ? 'light' : 'dark');
  }

  private applyTheme(theme: Theme): void {
    this.theme.set(theme);
    this.document.documentElement.setAttribute('data-theme', theme);
  }

  private getStoredTheme(): Theme | null {
    if (!globalThis.window) {
      return null;
    }

    try {
      const stored = globalThis.localStorage.getItem(this.storageKey);
      return stored === 'light' || stored === 'dark' ? stored : null;
    } catch {
      return null;
    }
  }

  private storeTheme(theme: Theme): void {
    if (!globalThis.window) {
      return;
    }

    try {
      globalThis.localStorage.setItem(this.storageKey, theme);
    } catch {
      // Almacenamiento no disponible: el tema solo dura la sesión.
    }
  }

  private getSystemTheme(): Theme {
    if (!globalThis.window || typeof globalThis.matchMedia !== 'function') {
      return 'light';
    }

    return globalThis.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
}
